import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Plus } from 'lucide-react';

import type { ClientOptionModel, ProjectOptionModel } from '@/domain/models/user.js';
import { UserAssignmentCard } from '@/presentation/pages/users/components/tabs/user-assignment-card.js';
import type { AssignmentRow } from '@/presentation/pages/users/utils/validate-user-form.js';
import { formatDate } from '@/shared/utils/format-date.js';
import type { UserProfile } from '@/shared/utils/format-status.js';

interface Props {
    assignments: AssignmentRow[];
    clients: ClientOptionModel[];
    projectsMap: Record<string, ProjectOptionModel[]>;
    profiles: { value: UserProfile; label: string }[];
    errors: Record<string, string>;
    isEdit: boolean;
    lastLoginAt: string | null;
    labels: {
        assignments: string;
        addAssignment: string;
        lastLogin: string;
        client: string;
        projectOptional: string;
        profileLabel: string;
        selectClient: string;
        allProjectsOption: string;
        removeAssignment: string;
    };
    onAdd: () => void;
    onUpdate: (index: number, field: keyof AssignmentRow, value: string) => void;
    onRemove: (index: number) => void;
}

export function UserAssignmentsTab({ assignments, clients, projectsMap, profiles, errors, isEdit, lastLoginAt, labels, onAdd, onUpdate, onRemove }: Props) {
    return (
        <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Typography variant="subtitle1" fontWeight={600} color="text.primary">
                    {labels.assignments}
                </Typography>
                <Button variant="outlined" size="small" startIcon={<Plus size={14} />} onClick={onAdd} sx={{ textTransform: 'none' }}>
                    {labels.addAssignment}
                </Button>
            </Stack>
            {errors.assignments && (
                <Typography variant="caption" color="error.main" sx={{ mb: 1.5, display: 'block' }}>
                    {errors.assignments}
                </Typography>
            )}
            <Stack spacing={1.5}>
                {assignments.map((a, i) => (
                    <UserAssignmentCard
                        key={a.id}
                        assignment={a}
                        clients={clients}
                        projects={projectsMap[a.clientId] ?? []}
                        profiles={profiles}
                        canRemove={assignments.length > 1}
                        error={errors[`profile_${i}`]}
                        labels={{
                            client: labels.client,
                            projectOptional: labels.projectOptional,
                            profileLabel: labels.profileLabel,
                            selectClient: labels.selectClient,
                            allProjectsOption: labels.allProjectsOption,
                            removeAssignment: labels.removeAssignment
                        }}
                        onChange={(field, value) => onUpdate(i, field, value)}
                        onRemove={() => onRemove(i)}
                    />
                ))}
            </Stack>

            {isEdit && assignments.length > 0 && (
                <Box sx={{ mt: 3 }}>
                    <Typography variant="caption" color="text.disabled">
                        {labels.lastLogin}: {formatDate(lastLoginAt)}
                    </Typography>
                </Box>
            )}
        </Box>
    );
}
